<template>
  <div v-if="study">
    <Breadcrumb :items="crumbs" />

    <section class="container hero">
      <span class="pill">{{ study.industry }}</span>
      <h1 class="h-title">{{ study.title }}</h1>
      <p class="client">{{ study.clientLabel }}<template v-if="study.location"> · {{ study.location }}</template></p>
      <ul v-if="study.services.length" class="tags" :aria-label="labels.services">
        <li v-for="service in study.services" :key="service" class="tag">{{ service }}</li>
      </ul>
    </section>

    <section class="section container story">
      <div class="block">
        <h2 class="h2 heading">{{ labels.problem }}</h2>
        <p class="body">{{ study.problem }}</p>
      </div>

      <div class="block">
        <h2 class="h2 heading">{{ labels.built }}</h2>
        <ul class="bullets">
          <li v-for="(item, index) in study.solution" :key="index">
            <span class="dot" aria-hidden="true"></span>
            {{ item }}
          </li>
        </ul>
      </div>

      <div class="block">
        <h2 class="h2 heading">{{ labels.result }}</h2>
        <p class="body">{{ study.result }}</p>
      </div>
    </section>

    <section v-if="study.metrics.length" class="section container">
      <div class="on-dark-panel">
        <h2 class="visually-label">{{ labels.result }}</h2>
        <ul class="metrics">
          <li v-for="metric in study.metrics" :key="metric.label" class="metric-tile">
            <span class="value">{{ metric.value }}</span>
            <span class="label">{{ metric.label }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="section container story">
      <div class="block">
        <h2 class="h2 heading">{{ labels.stack }}</h2>
        <ul class="tags">
          <li v-for="item in study.stack" :key="item" class="tag" dir="auto">{{ item }}</li>
        </ul>
        <p v-for="link in study.links" :key="link.url" class="external">
          <a :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.label }} {{ arrow }}</a>
        </p>
      </div>

      <div v-if="relatedSolutions.length" class="block">
        <h2 class="h2 heading">{{ labels.related }}</h2>
        <div class="buttons">
          <PillButton v-for="item in relatedSolutions" :key="item.slug" :to="`/products/${item.slug}`" variant="secondary" size="md">{{ pick(item, 'name') }} {{ arrow }}</PillButton>
        </div>
      </div>
    </section>

    <section class="section container">
      <div class="cta">
        <h2 class="h2 cta-title">{{ t('closing.title') }}</h2>
        <p class="cta-sub">{{ t('closing.sub') }}</p>
        <div class="buttons">
          <PillButton to="/contact" size="lg">{{ labels.discuss_similar }}</PillButton>
          <PillButton :href="bookingUrl" target="_blank" rel="noopener noreferrer" variant="outline" size="lg">{{ copy.bookCall }}</PillButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Breadcrumb from '../components/Breadcrumb.vue'
import PillButton from '../components/PillButton.vue'
import { useLocale } from '../composables/useLocale'
import { useCaseStudies } from '../composables/useCaseStudies'
import { usePageMeta } from '../composables/usePageMeta'
import { solutions } from '../data'
import { BOOKING_URL as bookingUrl, CONVERSION_COPY } from '../config/site'

const route = useRoute()
const router = useRouter()
const { locale, arrow, t, ui, pick, to } = useLocale()
const { find, labels, seo } = useCaseStudies()

const copy = computed(() => CONVERSION_COPY[locale.value] || CONVERSION_COPY.en)
const study = computed(() => find(route.params.slug))

// Unknown, unpublished or still-incomplete studies go back to the list.
watchEffect(() => {
  if (!study.value) {
    router.replace(to('/case-studies'))
  }
})

const relatedSolutions = computed(() => (study.value?.relatedProductSlugs || [])
  .map((slug) => solutions.find((item) => item.slug === slug))
  .filter(Boolean))

const crumbs = computed(() => [
  { label: ui('home'), to: '/' },
  { label: t('nav.work'), to: '/case-studies' },
  { label: study.value?.title }
])

const meta = computed(() => (study.value ? seo(study.value, t('brand')) : null))
usePageMeta(
  computed(() => meta.value?.title),
  computed(() => meta.value?.description)
)
</script>

<style scoped>
.hero {
  padding-top: clamp(32px, 6vw, 64px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
}

.client {
  font-size: 17px;
  color: var(--muted);
  margin: 0;
}

.tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.story {
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: clamp(32px, 5vw, 56px);
}

.heading {
  font-size: clamp(28px, 3.4vw, 40px);
  margin-bottom: 18px;
}

.body {
  font-size: 18px;
  line-height: 1.7;
  color: var(--muted);
  margin: 0;
}

.bullets {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bullets li {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: var(--surface);
  border-radius: var(--radius-inner);
  padding: 18px 22px;
  font-size: 17px;
  line-height: 1.6;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: var(--gold-on-dark);
  flex-shrink: 0;
  margin-top: 0.5em;
}

.external {
  margin: 20px 0 0;
  font-weight: 500;
}

.external a {
  text-decoration: underline;
  text-underline-offset: 4px;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.visually-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.metrics {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 24px;
}

.metric-tile {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.value {
  font-family: var(--font-display);
  font-weight: 300;
  font-size: clamp(44px, 5vw, 68px);
  line-height: 1;
  color: var(--gold-on-dark);
}

.label {
  color: var(--on-dark-muted);
  font-size: 15px;
}

.cta {
  background: var(--sand);
  border-radius: var(--radius-panel);
  padding: clamp(40px, 7vw, 72px) clamp(24px, 4vw, 56px);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.cta-title {
  font-size: clamp(32px, 5vw, 56px);
  margin: 0;
  max-width: 16ch;
}

.cta-sub {
  font-size: 18px;
  line-height: 1.6;
  color: var(--muted);
  max-width: 44ch;
  margin: 0;
}

.cta .buttons {
  justify-content: center;
}
</style>
